import { Modal, Box, SpaceBetween, Button, KeyValuePairs, Link, StatusIndicator, Textarea, Checkbox, Alert, AlertProps, RadioGroup, FormField } from '@amzn/awsui-components-react';
import { set } from 'date-fns';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CalculationComment, CalculationReviewApplyCommentType, OpenCalculationReview, SaveOpenCalculationReviewNotificationCommentResponse } from 'src/models/dashboard/OpenCalculationReview';
import EntityStatusUtil from 'src/services/common/EntityStatusUtil';
import CONSTANTS from 'src/utils/constants';
import TPENavigator from '../TPENavigator';
import StringUtils from 'src/utils/stringUtils';
import DASHBOARD_CONSTANTS from './dashboardConstants';
import NotificationComment from 'src/models/dashboard/NotificationComment';

export default function OpenCalculationReviewCommentModal (props:{
    calculationReview: OpenCalculationReview,
    comments: NotificationComment[],
    visible: boolean,
    isSavingComment: boolean,
    saveCommentError: string,
    saveNotificationCommentResult?: SaveOpenCalculationReviewNotificationCommentResponse,
    onClose: () => void,
    onSaveComment: (calculationReview: OpenCalculationReview, comment: CalculationComment) => void
}){
    const MAX_REASON_LENGTH_ALLOWED: number = 200;
    const { calculationReview, comments, visible, isSavingComment, saveCommentError, saveNotificationCommentResult, onClose, onSaveComment } = props;
    const [navigateURL, setNavigateURL] = useState('');
    
    const navigateToCalcBuilder = (calculationNumber: string, recordId: string) => {
        if (recordId != null) {
            setNavigateURL(CONSTANTS.PAGE_NAV.CALCULATION_BUILDER.URL + '/' + btoa(recordId));
        } else {
            setNavigateURL(CONSTANTS.PAGE_NAV.ICRS_CALCULATION_BUILDER.URL + '/' + btoa(calculationNumber));
        }
    }

    const [newCommentText, setNewCommentText] = React.useState("");
    const [applyCommentToType, setApplyCommentToType] = React.useState<CalculationReviewApplyCommentType | null>(null);
    const [selectedCommentOption, setSelectedCommentOption] = React.useState(null as string | null);
    const [commentCharCount, setCommentCharCount] = React.useState(0);
    
    const [alertHeaderText, setAlertHeaderText] = React.useState("");
    const [alertBodyText, setAlertBodyText] = React.useState("");
    const [alertType, setAlertType] = React.useState("info" as AlertProps.Type);
    const [showAlert, setShowAlert] = React.useState(false);
    const [isConfirmed, setIsConfirmed] = React.useState(false);
    const [needsConfirmation, setNeedsConfirmation] = React.useState(false);
    const addingCommentsDisabled = DASHBOARD_CONSTANTS.COMPLETE_STATUS === calculationReview.taskStatus;

    useEffect(() => {
        setCommentCharCount(newCommentText?.length || 0);
    }, [newCommentText])

    useEffect(() => {
        if (saveNotificationCommentResult != null && saveNotificationCommentResult.commentsSaved > 0){
            setAlertHeaderText("Success");
            setAlertBodyText(`${saveNotificationCommentResult.commentsSaved === 1? "Comment" : `${saveNotificationCommentResult.commentsSaved} comments`} saved successfully`);
            setAlertType("success");
            setNewCommentText("");
            setApplyCommentToType(null);
            setSelectedCommentOption(null);
            setShowAlert(true);
            setTimeout(() => {
                setShowAlert(false);
            }, 3000)
        }
    }, [saveNotificationCommentResult])

    useEffect(() => {
        if(StringUtils.isNullOrEmpty(saveCommentError)){
            return;
        }
        setAlertHeaderText("Error");
        setAlertBodyText(saveCommentError);
        setAlertType("error");
        setShowAlert(true);
    }, [saveCommentError])

    useEffect(() => {
        if (commentCharCount >= MAX_REASON_LENGTH_ALLOWED){
            setNewCommentText(newCommentText.substring(0, MAX_REASON_LENGTH_ALLOWED));
        }
    }, [commentCharCount])

    useEffect(() =>{
        setNewCommentText("");
        setApplyCommentToType(null);
        setShowAlert(false);
        setIsConfirmed(false);
        setNeedsConfirmation(false);
    }, [visible])

    useEffect(() => {
        setNeedsConfirmation(false);
        if (applyCommentToType != null && (applyCommentToType === CalculationReviewApplyCommentType.CWB || applyCommentToType === CalculationReviewApplyCommentType.Template)){
            setAlertHeaderText("New comment will apply to all CLIs in the CLI's CWB or template");
            setAlertBodyText("Please confirm and click Save");
            setAlertType("warning");
            setShowAlert(true);
            setNeedsConfirmation(true);
        }
        else if (applyCommentToType != null) {
            setAlertHeaderText("");
            setAlertBodyText("");
            setAlertType("info");
            setShowAlert(false);
        }
    }, [applyCommentToType]);

    const onSaveClickHandler = () => {
        if (needsConfirmation && !isConfirmed){
            return;
        }
        var validationCounter = 0;
        if (newCommentText.trim() == ""){
            setAlertHeaderText("");
            setAlertBodyText("Comment field cannot be empty");
            setAlertType("error");
            setShowAlert(true);
            validationCounter++;
        }
        if(applyCommentToType == null){
            setAlertHeaderText("");
            setAlertBodyText("Please select at least one option");
            setAlertType("error");
            setShowAlert(true);
            validationCounter++;
        }
        if (validationCounter === 2){
            setAlertHeaderText("");
            setAlertBodyText("Comment field and review option cannot be empty");
            setAlertType("error");
            setShowAlert(true);                        
        }
        if (validationCounter === 0){
            var comment: CalculationComment = {
                comment: newCommentText,
                commentedBy: 'User',
                commentedOn: Date.now(),
                commentedOnDisplay: new Date().toISOString(),
                applyCommentType: applyCommentToType == null? CalculationReviewApplyCommentType.CLI : applyCommentToType
            }
            onSaveComment(calculationReview, comment);            
        }
    }
    
    return (
        <Modal
            onDismiss={onClose}
            visible={visible}
            size='large'
            footer={
                <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={onSaveClickHandler} disabled={addingCommentsDisabled || isSavingComment}>{isSavingComment? 'Saving...': 'Save'}</Button>
                </SpaceBetween>
                </Box>
            }
            header="Comment"
            >
            <KeyValuePairs
                        columns={3}
                        items={[
                          { label: "CLI", value: <Link href="javascript:void(0);" onFollow={() => navigateToCalcBuilder(calculationReview.calculationNumber, calculationReview.recordId)}>{calculationReview.calculationNumber}</Link> },
                          { label: "ATP CLI status", value: <StatusIndicator type={EntityStatusUtil.mapToStatusIndicatorBy(calculationReview.atpCalculationStatus)}>{calculationReview.atpCalculationStatus}</StatusIndicator> },
                          { label: "Apttus CLI status", value: <StatusIndicator type={EntityStatusUtil.mapToStatusIndicatorBy(calculationReview.apttusCalculationStatus)}>{calculationReview.apttusCalculationStatus}</StatusIndicator> },
                        ]}
                      />
            <SpaceBetween direction='vertical' size='l'>
                <div className='comments-list'>
                    <SpaceBetween direction='vertical' size='s'>
                        {comments.map(x => <div>
                            <SpaceBetween direction='horizontal' size='s'><span>{x.user}</span><small>{x.commentedDate}</small></SpaceBetween>
                            {x.comment}
                        </div>)}
                    </SpaceBetween>
                </div>
                { !addingCommentsDisabled && <FormField label="Add comment for the current MEC period" description={`(${commentCharCount} of ${MAX_REASON_LENGTH_ALLOWED} characters allowed)`} stretch>
                    <Textarea
                        onChange={({ detail }) => setNewCommentText(detail.value)}
                        value={newCommentText}
                        placeholder="Enter comment here"
                        />
                    </FormField>
                }
                { !addingCommentsDisabled &&
                    <div>
                        <SpaceBetween direction='vertical' size='xxs'>
                            <strong>Select review option</strong>
                            <RadioGroup
                                onChange={({ detail }) => {
                                    var value = detail.value;
                                    setSelectedCommentOption(value);
                                    setApplyCommentToType(CalculationReviewApplyCommentType[value as keyof typeof CalculationReviewApplyCommentType]); 
                                }}
                                value={selectedCommentOption}
                                items={[
                                    { value: "CWB", label: " Apply comment to all CLIs in CWB" },
                                    { value: "Template", label: "Apply comment to all CLIs in template" },
                                    { value: "CLI", label: "Persist comment to this individual CLI only" }
                                ]}
                            />
                        </SpaceBetween>
                    </div>
                }
                { showAlert &&
                    <Alert
                        type={alertType}
                        header={alertHeaderText}
                        action={needsConfirmation && (isConfirmed? <Button iconAlign='right' iconName='check' disabled={true}>Confirmed</Button> : <Button onClick={() => setIsConfirmed(true)}>Confirm</Button>)}
                        >
                        {alertBodyText}
                    </Alert>
                }
            </SpaceBetween>
            <TPENavigator path={navigateURL} />
        </Modal>
    )

}